import React, { useEffect, useMemo, useState } from "react";
import { Tabs, Button, Input, Form, notification } from "antd";
import { ReloadOutlined } from "@ant-design/icons";

import PageListActionBar, {
  ExtraTabsContent,
} from "../../../../components/layout/page.actionBar";
import { useLocation } from "react-router-dom";
import QuizQuestionFormBuilder, { FormType } from "../quiz/form.builder";
import {
  getCurrentElementPosition,
  PageNavigation,
} from "../../../../components/navigation";
import Scenario from "./create.scenario";
import { useAtom } from "jotai";
import {
  cpdContentCaseById,
  cpdQuizContentByIdQueryAtom,
} from "../../../../services/queries/learning";
import {
  cpdContentCasesState,
  cpdContentCaseState,
  cpdContentQuizState,
} from "../../../../state";
import { TabPane } from "../tabs";
import { getThemesFromCases } from "./scenario";
import {
  createOrUpdateCase,
  deleteQuizQuestionById,
} from "../../../../services";

export const ActionType = {
  CREATE_NEW_CASE: "CREATE_NEW_CASE",
  UPDATE_CASE: "UPDATE_CASE",
};

export default function CpdCasePage() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isPreviewOn, setIsPreviewOn] = useState(false);
  const [activeTab, setActiveTab] = useState("Case"); // Default to Overview tab

  const [form] = Form.useForm();
  const location = useLocation();
  // const navigate = useNavigate();

  const [notificationApi, notificationCtxHolder] =
    notification.useNotification();

  const [cases, setCases] = useAtom(cpdContentCasesState, {
    delay: 0,
  });

  const [currentElement, setCurrentElement] = useState(location?.state?.case);
  const [currentPageIndex, setCurrentPageIndex] = useState(
    getCurrentElementPosition(location?.state?.case, cases)
  );

  const [caseObject, setCase] = useAtom(cpdContentCaseState, {
    delay: 0,
  });
  const cpdContentCaseByIdQueryAtom = useMemo(() => {
    return cpdContentCaseById(`${currentElement?.uid}`);
  }, [currentElement]);
  const [{ data }] = useAtom(cpdContentCaseByIdQueryAtom, {
    delay: 0,
    refetchOnWindowFocus: true,
  });

  const [quiz, setQuiz] = useAtom(cpdContentQuizState, {
    delay: 0,
  });
  const cpdQuizForCaseByIdQueryAtom = useMemo(() => {
    return cpdQuizContentByIdQueryAtom(
      `${currentElement?.uid}`,
      FormType?.CASE
    );
  }, [currentElement]);
  const [{ data: quizData, refetch }] = useAtom(cpdQuizForCaseByIdQueryAtom, {
    delay: 0,
  });

  useEffect(() => setQuiz(quizData), [quizData, setQuiz]);

  useEffect(() => {
    if (data && currentElement) {
      setCase({ ...currentElement, ...data });
      setCurrentElement({ ...currentElement, ...data });
    }
  }, currentElement);

  const handleTabChange = (key) => {
    switch (key) {
      case "0":
        setActiveTab("Case");
        break;
      case "1":
        setActiveTab("Question");
        break;
      default:
        break;
    }
  };

  const handleDeleteQuizQuestion = (question) => {
    const url = `/cpd/cases/${currentElement?.uid}/quiz/${question?.uid}`;
    deleteQuizQuestionById(url)
      .then((response) => {
        console.log(response);
        setTimeout(() => {
          notificationApi.open({
            key: "case-quiz-delete-success-notify",
            type: "success",
            message: "Request successful",
            description: "",
            showProgress: true,
            pauseOnHover: true,
          });
          refetch(); // refresh page with new changes
        }, 50);
      })
      .catch((error) => {
        setTimeout(() => {
          notificationApi.open({
            key: "case-quiz-delete-error-notify",
            type: "error",
            message: "Request failed to update",
            description: `${error}`,
            showProgress: true,
            pauseOnHover: true,
          });
        }, 50);
      });
  };

  const handleSubmitQuizQuestion = (data) => {
    const payload = {
      title: currentElement?.title
        ? currentElement?.title
        : `${currentElement?.title} QUIZ`,
      uid: currentElement?.uid,
      themeUid: currentElement?.theme?.uid,
      status: currentElement?.status,
      questions: [{ ...data }],
    };

    createOrUpdateCase({
      body: { ...payload },
    })
      .then((response) => {
        setQuiz(response?.data);
        setTimeout(() => {
          notificationApi.open({
            key: "case-quiz-update-success-notify",
            type: "success",
            message: "Request successful",
            description: "",
            showProgress: true,
            pauseOnHover: true,
          });
          refetch(); // refresh page with new changes
        }, 50);
      })
      .catch((error) => {
        setTimeout(() => {
          notificationApi.open({
            key: "case-quiz-update-error-notify",
            type: "error",
            message: "Request failed to update",
            description: `${error}`,
            showProgress: true,
            pauseOnHover: true,
          });
        }, 50);
      });
  };

  return (
    <React.Fragment>
      {notificationCtxHolder}
      <Tabs
        defaultActiveKey="0"
        tabBarExtraContent={
          <ExtraTabsContent
            form={form}
            isPreviewOn={isPreviewOn}
            setIsDrawerOpen={setIsDrawerOpen}
            setIsPreviewOn={setIsPreviewOn}
            buttonText={activeTab}
          />
        }
        onChange={handleTabChange}
      >
        <Tabs.TabPane tab="Case Details" key="0">
          <TabPane>
            <Scenario
              currentElement={currentElement}
              isPreviewOn={isPreviewOn}
              setIsPreviewOn={setIsPreviewOn}
              themes={getThemesFromCases({ cases })}
            />
          </TabPane>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Quiz" key="1">
          <PageListActionBar
            form={form}
            leftElement={() => (
              <div className="flex items-center gap-2">
                <Input.Search
                  placeholder="Filter questions"
                  allowClear
                  style={{ maxWidth: 260 }}
                />
                <Button
                  type="dashed"
                  icon={<ReloadOutlined />}
                  onClick={refetch}
                />
              </div>
            )}
          />

          <QuizQuestionFormBuilder
            quiz={quiz}
            isDrawerOpen={isDrawerOpen}
            setIsDrawerOpen={setIsDrawerOpen}
            isPreviewOn={isPreviewOn}
            onSubmitQuestion={handleSubmitQuizQuestion}
            onDeleteQuestion={handleDeleteQuizQuestion}
          />
        </Tabs.TabPane>
      </Tabs>
      {!isPreviewOn && (
        <PageNavigation
          setCurrentElement={setCurrentElement}
          currentPageIndex={currentPageIndex}
          setCurrentPageIndex={setCurrentPageIndex}
          elements={cases}
          previousLabel="Previous Case"
          nextLabel="Next Case"
        />
      )}
    </React.Fragment>
  );
}
